import { objectKeys } from "@appsmith/utils";
import type { UpdateWidgetPropertyPayload } from "actions/controlActions";
import { modifyMetaWidgetsWithoutEval } from "actions/metaWidgetActions";
import type {
  MultipleWidgetDeletePayload,
  WidgetDelete,
} from "actions/pageActions";
import type { ReduxAction } from "actions/ReduxActionTypes";
import type { ApiResponse } from "api/ApiResponses";
import { getCanvasWidgets } from "ce/selectors/entitiesSelector";
import { MAIN_CONTAINER_WIDGET_ID } from "constants/WidgetConstants";
import {
  deleteModuleInstance,
  updateModuleInstance,
} from "ee/actions/moduleInstanceActions";
import type { CreateModuleInstanceResponse } from "ee/api/ModuleInstanceApi";
import type { PullPackageResponse } from "ee/api/PackageApi";
import { MODULE_TYPE } from "ee/constants/ModuleConstants";
import type { ModuleInstance } from "ee/constants/ModuleInstanceConstants";
import type { Layout } from "ee/constants/ModuleLayoutConstants";
import {
  ReduxActionErrorTypes,
  ReduxActionTypes,
} from "ee/constants/ReduxActionConstants";
import { ENTITY_TYPE, PLATFORM_ERROR } from "ee/entities/AppsmithConsole/utils";
import type { FlattenedWidgetProps } from "ee/reducers/entityReducers/canvasWidgetsReducer";
import { getShowUIModule } from "ee/selectors/moduleFeatureSelectors";
import {
  getAllModuleInstances,
  getGroupedModuleInstancesByWidgetId,
  getUiModuleInstances,
} from "ee/selectors/moduleInstanceSelectors";
import { MAIN_MODULE_CONTAINER_WIDGET_ID } from "ee/utils/Packages/createBaseModuleDSL";
import { recursivelyGenerateMetaWidgets } from "ee/utils/Packages/moduleInstanceHelpers";
import moduleWidgetFactory from "ee/widgets/ModuleWidgetFactory";
import { Severity, type Message } from "entities/AppsmithConsole";
import LOG_TYPE from "entities/AppsmithConsole/logtype";
import { klona } from "klona";
import { get, set } from "lodash";
import log from "loglevel";
import {
  all,
  call,
  delay,
  put,
  select,
  take,
  takeEvery,
  takeLatest,
} from "redux-saga/effects";
import { failFastApiCalls } from "sagas/InitSagas";
import { getSelectedWidget, getWidgets } from "sagas/selectors";
import { getIsFetchingPage, getMetaWidgets } from "selectors/editorSelectors";
import { getSelectedWidgets } from "selectors/ui";
import AppsmithConsole, { type ErrorObject } from "utils/AppsmithConsole";
import { isDynamicValue, type DynamicPath } from "utils/DynamicBindingUtils";
import WidgetFactory from "WidgetProvider/factory";
import { registerWidgets } from "WidgetProvider/factory/registrationHelper";
import type { WidgetProps } from "widgets/BaseWidget";

/**
 * Checks if a widget has changed based on property updates
 */
function hasWidgetChanged(
  widget: WidgetProps,
  mainContainerWidget: WidgetProps,
  propertyUpdates: { propertyPath: string; propertyValue: unknown }[],
) {
  let hasChanged = false;

  propertyUpdates.forEach((update) => {
    const updatedValue = get(mainContainerWidget, update.propertyPath);

    if (widget[update.propertyPath] !== updatedValue) {
      hasChanged = true;
    }
  });

  return hasChanged;
}

export function* registerUIModuleInstanceIfNotRegisteredSaga(
  moduleInstance: ModuleInstance,
) {
  const widgetConfigs = WidgetFactory.getConfigs();

  const moduleWidgetType = "MODULE_WIDGET_" + moduleInstance.moduleUUID;
  const moduleWidgetConfig = widgetConfigs[moduleWidgetType];

  if (!moduleWidgetConfig) {
    yield call(
      registerUIModuleInstance,
      moduleInstance as ModuleInstance<Layout>,
    );
  }
}

/**
 * Registers a UI module instance with the widget factory
 */
export function* registerUIModuleInstance(
  moduleInstance: ModuleInstance<Layout>,
) {
  try {
    if (moduleInstance.type !== MODULE_TYPE.UI) {
      return false;
    }

    const dsl = moduleInstance.layouts?.[0]?.dsl;

    const container = (dsl?.children || []).find(
      (child: WidgetProps) =>
        child.widgetId === MAIN_MODULE_CONTAINER_WIDGET_ID,
    );

    let instanceMetadata = { rows: 0, columns: 0, hideCard: true };

    if (container) {
      instanceMetadata = {
        rows: container?.bottomRow - container?.topRow,
        columns: container?.rightColumn - container?.leftColumn,
        hideCard: false,
      };
    }

    registerWidgets([
      // TODO: Fix this
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      moduleWidgetFactory({
        ...instanceMetadata,
        moduleUUID: moduleInstance.moduleUUID,
        widgetName: moduleInstance.name,
      }),
    ]);

    return true;
  } catch (error) {
    log.error("Error registering UI module instance:", error);
  }
}

/**
 * Generates meta widgets for a UI module instance
 */
export function* generateUIModuleMetaWidgetsSaga(
  action: ReduxAction<{ moduleInstance: ModuleInstance<Layout> }>,
) {
  try {
    const { moduleInstance } = action.payload;
    const flattenedMetaWidgets: Record<string, FlattenedWidgetProps> = {};
    const rootContainerWidget = moduleInstance.layouts?.[0]?.dsl;

    const mainContainerWidget = rootContainerWidget?.children?.find(
      (child) => child.widgetId === MAIN_MODULE_CONTAINER_WIDGET_ID,
    );

    const modalWidgets = rootContainerWidget?.children?.filter(
      (child) => child.type === "MODAL_WIDGET",
    );

    if (!mainContainerWidget) {
      throw new Error("Main container widget not found");
    }

    if (!moduleInstance.widgetId) {
      throw new Error("Module instance widget id not found");
    }

    recursivelyGenerateMetaWidgets({
      moduleInstanceName: moduleInstance.name,
      widgets: mainContainerWidget.children as WidgetProps[],
      flattenedWidgets: flattenedMetaWidgets,
      parentId: moduleInstance.widgetId,
      creatorId: moduleInstance.widgetId,
    });

    const modalMetaWidgetsIds = recursivelyGenerateMetaWidgets({
      moduleInstanceName: moduleInstance.name,
      widgets: modalWidgets as WidgetProps[],
      flattenedWidgets: flattenedMetaWidgets,
      parentId: MAIN_CONTAINER_WIDGET_ID,
      creatorId: moduleInstance.widgetId,
    });

    modalMetaWidgetsIds.forEach((modalWidgetId) => {
      if (flattenedMetaWidgets[modalWidgetId]) {
        flattenedMetaWidgets[modalWidgetId].hasMetaWidgets = true;
      }
    });

    // Adding a minimal delay (1ms) to ensure this success action is dispatched
    // after the race effect in failFastApiCalls is set up and listening.
    // Without this delay, the success action might be dispatched too quickly,
    // before the race effect has a chance to register, causing failFastApiCalls
    // to miss the success action. This tiny delay yields control back to the
    // event loop, allowing other saga effects to complete their setup.
    yield delay(1);

    yield put(
      modifyMetaWidgetsWithoutEval({
        addOrUpdate: flattenedMetaWidgets,
        deleteIds: [],
        propertyUpdates: [],
        creatorId: moduleInstance.widgetId,
      }),
    );

    yield put({
      type: ReduxActionTypes.GENERATE_UI_MODULE_META_WIDGETS_SUCCESS,
    });
  } catch (error) {
    yield put({
      type: ReduxActionErrorTypes.GENERATE_UI_MODULE_META_WIDGETS_ERROR,
      payload: {
        error,
        show: false,
      },
    });
  }
}

/**
 * Updates the root module instance widget properties
 */
export function* updateRootModuleInstanceWidgetSaga(
  action: ReduxAction<{
    moduleInstance: ModuleInstance<Layout>;
  }>,
) {
  try {
    const { moduleInstance } = action.payload;
    const widgets: ReturnType<typeof getCanvasWidgets> =
      yield select(getCanvasWidgets);

    if (!moduleInstance.widgetId) {
      throw new Error("Module instance widget id not found");
    }

    const widget = widgets[moduleInstance.widgetId];
    const rootContainerWidget = moduleInstance.layouts?.[0]?.dsl;
    const mainContainerWidget = rootContainerWidget?.children?.find(
      (child) => child.widgetId === MAIN_MODULE_CONTAINER_WIDGET_ID,
    );

    if (!widget || !mainContainerWidget) {
      throw new Error("Root module instance widget not found");
    }

    const propertiesToUpdate = [
      "boxShadow",
      "borderColor",
      "shouldScrollContents",
      "animateLoading",
      "borderWidth",
      "flexVerticalAlignment",
      "backgroundColor",
      "dynamicHeight",
      "containerStyle",
      "minWidth",
      "isVisible",
      "isLoading",
      "responsiveBehavior",
      "borderRadius",
      "maxDynamicHeight",
      "minDynamicHeight",
    ];

    const propertyUpdates = propertiesToUpdate.map((prop) => ({
      propertyPath: prop,
      propertyValue: mainContainerWidget?.[prop],
    }));

    const dynamicBindingPathList: DynamicPath[] = [];

    objectKeys(moduleInstance.inputs).forEach((moduleInstanceInput) => {
      if (isDynamicValue(moduleInstance.inputs[moduleInstanceInput])) {
        dynamicBindingPathList.push({
          key: `inputs.${moduleInstanceInput}`,
        });
      }
    });

    if (moduleInstance?.outputs) {
      objectKeys(moduleInstance?.outputs).forEach((moduleInstanceOutput) => {
        if (
          isDynamicValue(moduleInstance.outputs?.[moduleInstanceOutput] || "")
        ) {
          dynamicBindingPathList.push({
            key: `outputs.${moduleInstanceOutput}`,
          });
        }
      });
    }

    // Add moduleInstanceId property update
    propertyUpdates.push({
      propertyPath: "widgetName",
      propertyValue: moduleInstance.name,
    });

    propertyUpdates.push({
      propertyPath: "inputs",
      propertyValue: moduleInstance.inputs,
    });

    propertyUpdates.push({
      propertyPath: "outputs",
      propertyValue: moduleInstance.outputs,
    });

    propertyUpdates.push({
      propertyPath: "dynamicBindingPathList",
      propertyValue: dynamicBindingPathList,
    });

    propertyUpdates.push({
      propertyPath: "moduleInputs",
      propertyValue: moduleInstance.moduleInputs,
    });

    if (!hasWidgetChanged(widget, mainContainerWidget, propertyUpdates)) {
      // No changes detected, still mark as success
      yield put({
        type: ReduxActionTypes.UPDATE_ROOT_MODULE_INSTANCE_WIDGET_SUCCESS,
      });

      return;
    }

    yield put({
      type: ReduxActionTypes.UPDATE_MULTIPLE_WIDGET_PROPERTIES,
      payload: {
        widgetsToUpdate: {
          [moduleInstance.widgetId]: propertyUpdates,
        },
        shouldEval: false,
      },
    });

    // Adding a minimal delay (1ms) to ensure this success action is dispatched
    // after the race effect in failFastApiCalls is set up and listening.
    // Without this delay, the success action might be dispatched too quickly,
    // before the race effect has a chance to register, causing failFastApiCalls
    // to miss the success action. This tiny delay yields control back to the
    // event loop, allowing other saga effects to complete their setup.
    yield delay(1);

    yield put({
      type: ReduxActionTypes.UPDATE_ROOT_MODULE_INSTANCE_WIDGET_SUCCESS,
    });
  } catch (error) {
    yield put({
      type: ReduxActionErrorTypes.UPDATE_ROOT_MODULE_INSTANCE_WIDGET_ERROR,
      payload: {
        show: false,
        error,
      },
    });
  }
}

/**
 * Generates module widgets from instances for edit mode
 */
export function* generateModuleWidgetsFromInstancesSaga(
  action: ReduxAction<
    | {
        moduleInstances: ModuleInstance<Layout>[];
      }
    | undefined
  >,
) {
  try {
    const showUIModule: boolean = yield select(getShowUIModule);

    if (!showUIModule) {
      return;
    }

    let uiModuleInstances: ModuleInstance<Layout>[] =
      action?.payload?.moduleInstances || [];

    if (uiModuleInstances.length === 0) {
      uiModuleInstances = yield select(getUiModuleInstances<Layout>());
    }

    for (const moduleInstance of uiModuleInstances) {
      if (moduleInstance.type !== MODULE_TYPE.UI) {
        continue;
      }

      yield call(registerUIModuleInstanceIfNotRegisteredSaga, moduleInstance);

      const generationSuccess: boolean = yield failFastApiCalls(
        [
          {
            type: ReduxActionTypes.GENERATE_UI_MODULE_META_WIDGETS_INIT,
            payload: { moduleInstance: moduleInstance },
          },
          {
            type: ReduxActionTypes.UPDATE_ROOT_MODULE_INSTANCE_WIDGET_INIT,
            payload: { moduleInstance: moduleInstance },
          },
        ],
        [
          ReduxActionTypes.GENERATE_UI_MODULE_META_WIDGETS_SUCCESS,
          ReduxActionTypes.UPDATE_ROOT_MODULE_INSTANCE_WIDGET_SUCCESS,
        ],
        [
          ReduxActionErrorTypes.GENERATE_UI_MODULE_META_WIDGETS_ERROR,
          ReduxActionErrorTypes.UPDATE_ROOT_MODULE_INSTANCE_WIDGET_ERROR,
        ],
      );

      if (!generationSuccess) {
        log.error(
          `Failed generating module widgets for ${moduleInstance.name}`,
        );
      }
    }

    yield put({
      type: ReduxActionTypes.GENERATE_UI_MODULE_WIDGET_FROM_INSTANCES_SUCCESS,
    });
  } catch (error) {
    yield put({
      type: ReduxActionErrorTypes.GENERATE_UI_MODULE_WIDGET_FROM_INSTANCES_ERROR,
      payload: {
        show: false,
        error,
      },
    });
  }
}

export function* flushAllUIModuleMetaWidgetsSaga() {
  const moduleInstances: ReturnType<typeof getAllModuleInstances> =
    yield select(getAllModuleInstances);
  const metaWidgets: ReturnType<typeof getMetaWidgets> =
    yield select(getMetaWidgets);

  for (const moduleInstance of Object.values(moduleInstances)) {
    if (moduleInstance.type !== MODULE_TYPE.UI) {
      continue;
    }

    const metaWidgetsToDelete = Object.values(metaWidgets)
      .filter((metaWidget) => metaWidget.creatorId === moduleInstance.widgetId)
      .map((metaWidget) => metaWidget.widgetId);

    yield put(
      modifyMetaWidgetsWithoutEval({
        addOrUpdate: {},
        deleteIds: metaWidgetsToDelete,
        propertyUpdates: [],
        creatorId: moduleInstance.widgetId,
      }),
    );
  }
}

export function* deleteUIModuleInstanceSaga(action: ReduxAction<WidgetDelete>) {
  try {
    let { widgetId } = action.payload;
    const moduleInstances: ReturnType<typeof getAllModuleInstances> =
      yield select(getAllModuleInstances);

    if (!widgetId) {
      const selectedWidget: FlattenedWidgetProps | undefined =
        yield select(getSelectedWidget);

      if (!selectedWidget || selectedWidget.isDeletable === false) return;

      widgetId = selectedWidget.widgetId;
    }

    const moduleInstance = Object.values(moduleInstances).find(
      (moduleInstance) => moduleInstance.widgetId === widgetId,
    );

    if (!moduleInstance) {
      return;
    }

    yield put(
      deleteModuleInstance({
        id: moduleInstance.id,
        type: MODULE_TYPE.UI,
      }),
    );
  } catch (error) {
    yield put({
      type: ReduxActionErrorTypes.DELETE_UI_MODULE_INSTANCE_BY_WIDGET_ERROR,
      payload: error,
    });
  }
}

export function* deleteUIModuleInstanceBulkSaga(
  action: ReduxAction<MultipleWidgetDeletePayload>,
) {
  try {
    const { widgetIds = [] } = action.payload;
    const selectedWidgets: string[] = yield select(getSelectedWidgets);
    const widgets: ReturnType<typeof getWidgets> = yield select(getWidgets);
    const deletableWidgets = [...selectedWidgets, ...widgetIds].filter(
      (widgetId) => widgets[widgetId]?.isDeletable !== false,
    );

    if (deletableWidgets.length) {
      yield all(
        deletableWidgets.map((widgetId) =>
          put({
            type: ReduxActionTypes.DELETE_UI_MODULE_INSTANCE_BY_WIDGET_INIT,
            payload: {
              widgetId,
            },
          }),
        ),
      );
    }
  } catch (error) {
    yield put({
      type: ReduxActionErrorTypes.DELETE_UI_MODULE_INSTANCE_BY_WIDGET_ERROR,
      payload: error,
    });
  }
}

export function* processPullPackageSuccessSaga(
  action: ReduxAction<ApiResponse<PullPackageResponse>>,
) {
  const { moduleInstances } = action.payload.data;
  const isPageFetching: boolean = yield select(getIsFetchingPage);

  // If the page is fetching, we don't want to generate module widgets yet as the widgets
  // are not yet available in the DOM. Without this the main module widget won't be present
  // in the canvasWidgetReducer and the instance generation will fail.
  if (isPageFetching) {
    yield take(ReduxActionTypes.FETCH_PAGE_SUCCESS);
  }

  yield put({
    type: ReduxActionTypes.GENERATE_UI_MODULE_WIDGET_FROM_INSTANCES_INIT,
    payload: {
      moduleInstances,
    },
  });
}

export function* registerUIModuleInstancesInWidgetConfigSaga(
  action: ReduxAction<{ moduleInstances: ModuleInstance[] }>,
) {
  const { moduleInstances } = action?.payload;

  try {
    for (const moduleInstance of moduleInstances) {
      yield call(registerUIModuleInstanceIfNotRegisteredSaga, moduleInstance);
    }

    yield put({
      type: ReduxActionTypes.REGISTER_UI_MODULE_INSTANCES_IN_WIDGET_CONFIG_SUCCESS,
    });
  } catch (error) {
    yield put({
      type: ReduxActionErrorTypes.REGISTER_UI_MODULE_INSTANCES_IN_WIDGET_CONFIG_ERROR,
      payload: {
        error,
        show: false,
      },
    });
    log.error(
      "Error registering UI module instances in widget config",
      error,
      moduleInstances,
    );
  }
}

export function* logErrorsFromBrokenModuleInstancesSaga(
  action: ReduxAction<{ moduleInstances: Record<string, ModuleInstance> }>,
) {
  try {
    yield take(ReduxActionTypes.SWITCH_CURRENT_PAGE_ID);
    const moduleInstances = Object.values(action.payload.moduleInstances);
    const errorsToAdd: ErrorObject[] = [];

    for (const moduleInstance of moduleInstances) {
      if (moduleInstance.type !== MODULE_TYPE.UI) {
        continue;
      }

      const messages: Array<Message> = [];

      if (moduleInstance.invalids?.length > 0) {
        moduleInstance.invalids.forEach((invalid) => {
          messages.push({
            message: {
              name: "MissingModuleError",
              message: invalid,
            },
            type: PLATFORM_ERROR.MISSING_ENTITY,
            subType: "",
          });
        });

        errorsToAdd.push({
          payload: {
            id: moduleInstance.name,
            iconId: "",
            logType: LOG_TYPE.MISSING_MODULE,
            text: `Package Error: Unable to load module ${moduleInstance.name}`,
            messages: messages,
            source: {
              id: moduleInstance.widgetId || "",
              name: moduleInstance.name,
              type: ENTITY_TYPE.WIDGET,
              propertyPath: moduleInstance.widgetId,
              pluginType: `MODULE_WIDGET_${moduleInstance.moduleUUID}`,
            },
          },
          severity: Severity.ERROR,
        });
      }
    }

    AppsmithConsole.addErrors(errorsToAdd);
  } catch (error) {
    log.error("Error logging errors from broken module instances", error);
  }
}

export function* logErrorsForOrphanUIModuleInstancesSaga(
  action: ReduxAction<{ moduleInstances: Record<string, ModuleInstance> }>,
) {
  try {
    yield take(ReduxActionTypes.SWITCH_CURRENT_PAGE_ID);
    const moduleInstances = Object.values(action.payload.moduleInstances);

    const canvasWidgets: ReturnType<typeof getCanvasWidgets> =
      yield select(getCanvasWidgets);

    const errorsToAdd: ErrorObject[] = [];

    const orphanUIModuleInstances = moduleInstances.filter(
      (moduleInstance) =>
        moduleInstance.type === MODULE_TYPE.UI &&
        !canvasWidgets[moduleInstance.widgetId || ""],
    );

    for (const moduleInstance of orphanUIModuleInstances) {
      const messages: Array<Message> = [];

      messages.push({
        message: {
          name: "ReferenceError",
          message:
            "This module instance exists without a corresponding widget on the canvas. Review and delete it if it's not in use.",
        },
        type: PLATFORM_ERROR.MISSING_ENTITY,
        subType: "",
      });

      errorsToAdd.push({
        payload: {
          id: moduleInstance.id,
          iconId: "",
          logType: LOG_TYPE.MISSING_MODULE,
          text: `Unable to reference module instance ${moduleInstance.name}`,
          messages: messages,
          source: {
            id: moduleInstance.id || "",
            name: moduleInstance.name,
            type: ENTITY_TYPE.WIDGET,
            propertyPath: moduleInstance.widgetId,
            pluginType: `MODULE_WIDGET_${moduleInstance.moduleUUID}`,
          },
        },
        severity: Severity.ERROR,
      });
    }

    AppsmithConsole.addErrors(errorsToAdd);
  } catch (error) {
    log.error("Error logging errors for orphan UI module instances", error);
  }
}

export function* removeErrorsForOrphanUIModuleInstancesSaga(
  action: ReduxAction<{ id: string; type: MODULE_TYPE }>,
) {
  const { id, type } = action.payload;

  if (type !== MODULE_TYPE.UI) {
    return;
  }

  AppsmithConsole.deleteErrors([{ id }]);
}

export function* updateUIModuleInstanceInputsSaga(
  action: ReduxAction<{ updatesArray: UpdateWidgetPropertyPayload[] }>,
) {
  const { updatesArray } = action.payload;
  const groupedModuleInstancesByWidgetId: ReturnType<
    typeof getGroupedModuleInstancesByWidgetId
  > = yield select(getGroupedModuleInstancesByWidgetId);

  for (const updateItem of updatesArray) {
    const { updates, widgetId } = updateItem;
    const moduleInstance = groupedModuleInstancesByWidgetId[widgetId];

    if (!moduleInstance) {
      continue;
    }

    if (moduleInstance.type !== MODULE_TYPE.UI) {
      continue;
    }

    const updatedInputs = klona({ inputs: moduleInstance.inputs });

    for (const [propertyPath, propertyValue] of Object.entries(
      updates.modify || {},
    )) {
      set(updatedInputs, propertyPath, propertyValue);
    }

    yield put(
      updateModuleInstance({
        id: moduleInstance.id,
        moduleInstance: {
          inputs: updatedInputs.inputs,
        },
      }),
    );
  }
}

export function* handleCreateUIModuleInstanceSuccessSaga(
  action: ReduxAction<CreateModuleInstanceResponse>,
) {
  const { moduleInstance } = action.payload;

  yield put({
    type: ReduxActionTypes.GENERATE_UI_MODULE_WIDGET_FROM_INSTANCES_INIT,
    payload: {
      moduleInstances: [moduleInstance],
    },
  });
}

export default function* uiModuleInstanceSagas() {
  yield all([
    takeLatest(
      ReduxActionTypes.GENERATE_UI_MODULE_META_WIDGETS_INIT,
      generateUIModuleMetaWidgetsSaga,
    ),
    takeLatest(
      ReduxActionTypes.UPDATE_ROOT_MODULE_INSTANCE_WIDGET_INIT,
      updateRootModuleInstanceWidgetSaga,
    ),
    takeLatest(
      [
        // This gets called when page in loaded
        ReduxActionTypes.FETCH_PUBLISHED_PAGE_SUCCESS,
        // This gets called when page in switched
        ReduxActionTypes.FETCH_PUBLISHED_PAGE_RESOURCES_SUCCESS,
        ReduxActionTypes.FETCH_PAGE_SUCCESS,
        ReduxActionTypes.GENERATE_UI_MODULE_WIDGET_FROM_INSTANCES_INIT,
        ReduxActionTypes.CHANGE_CONSUMED_PACKAGE_VERSION_SUCCESS,
      ],
      generateModuleWidgetsFromInstancesSaga,
    ),
    takeLatest(
      [
        ReduxActionTypes.FETCH_PUBLISHED_PAGE_RESOURCES_INIT,
        ReduxActionTypes.SETUP_PAGE_INIT,
      ],
      flushAllUIModuleMetaWidgetsSaga,
    ),
    takeEvery(
      ReduxActionTypes.DELETE_UI_MODULE_INSTANCE_BY_WIDGET_INIT,
      deleteUIModuleInstanceSaga,
    ),
    takeEvery(
      ReduxActionTypes.DELETE_BULK_UI_MODULE_INSTANCE_BY_WIDGET_INIT,
      deleteUIModuleInstanceBulkSaga,
    ),
    takeLatest(
      ReduxActionTypes.PULL_PACKAGE_SUCCESS,
      processPullPackageSuccessSaga,
    ),
    takeEvery(
      ReduxActionTypes.REGISTER_UI_MODULE_INSTANCES_IN_WIDGET_CONFIG_INIT,
      registerUIModuleInstancesInWidgetConfigSaga,
    ),
    takeLatest(
      ReduxActionTypes.SETUP_MODULE_INSTANCE_SUCCESS,
      logErrorsFromBrokenModuleInstancesSaga,
    ),
    takeLatest(
      ReduxActionTypes.SETUP_MODULE_INSTANCE_SUCCESS,
      logErrorsForOrphanUIModuleInstancesSaga,
    ),
    takeLatest(
      ReduxActionTypes.DELETE_MODULE_INSTANCE_SUCCESS,
      removeErrorsForOrphanUIModuleInstancesSaga,
    ),
    takeEvery(
      ReduxActionTypes.BATCH_UPDATE_MULTIPLE_WIDGETS_PROPERTY,
      updateUIModuleInstanceInputsSaga,
    ),
    takeEvery(
      ReduxActionTypes.CREATE_MODULE_INSTANCE_SUCCESS,
      handleCreateUIModuleInstanceSuccessSaga,
    ),
  ]);
}
